<!-- Base.Tab.vue (client-lms) -->
<template>
  <div
    class="lms_tab_wrap"
    :class="[`lms_tab_${variant}`]"
    :style="{ '--tab-font-size': fontSizeMap[size] }"
    role="tablist"
  >
    <div
      v-for="(tab, index) in tabs"
      :key="index"
      class="lms_tab_item"
      :class="{
        active: selectedTab === tab.value,
        disabled: tab.disabled,
      }"
      role="tab"
      @click="!tab.disabled && selectTab(tab.value)"
    >
      <span class="lms_tab_label">{{ tab.label }}</span>
      <span v-if="tabCount && tabCount[index] !== undefined" class="lms_tab_count">{{
        tabCount[index]
      }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from "vue";

const selectedTab = defineModel<string>();

const props = defineProps({
  tabs: {
    type: Array<{ label: string; value: string; disabled?: boolean }>,
    default: () => [],
  },
  tabCount: {
    type: Array<number>,
    default: () => [],
  },
  variant: {
    type: String,
    default: "text", // 'text' or 'contained'
  },
  size: {
    type: String as PropType<"sm" | "md" | "lg">,
    default: "md",
  },
});

const fontSizeMap = { sm: "12px", md: "15px", lg: "18px" } as const;

const selectTab = (value: string) => {
  selectedTab.value = value;
};
</script>

<style scoped>
.lms_tab_wrap {
  display: flex;
  align-items: stretch;
  /* gap: 20px; */
}

.lms_tab_item {
  font-size: var(--tab-font-size, 15px);
}
 
/* Text Tab */
.lms_tab_text .lms_tab_item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
   cursor: pointer;
  position: relative;
  transition: color 0.2s;
  min-width: 100px;
  justify-content: center;
}

.lms_tab_text .lms_tab_label {
  font-family: "Pretendard", sans-serif;
  font-weight: 700;
  line-height: 24px;
  font-size: 18px;
  color: var(--lms-on-surface-low, rgba(0, 0, 0, 0.35));
}

.lms_tab_text .lms_tab_item.active .lms_tab_label {
  color: var(--lms-on-surface-high-var, rgba(0, 0, 0, 0.7));
}

.lms_tab_text .lms_tab_item.active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background-color: #5c74f6;
  border-radius: 3px 3px 0 0;
}

.lms_tab_text .lms_tab_item:hover:not(.active):not(.disabled) .lms_tab_label {
  color: var(--lms-on-surface-medium-var, rgba(0, 0, 0, 0.5));
}

/* Contained Tab */
.lms_tab_contained .lms_tab_item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 24px;
  cursor: pointer;
  border-radius: 4px;
  transition:
    background-color 0.2s,
    color 0.2s;
  background-color: var(--lms-grey-100, #f6f7f7);
  min-width: 100px;
  justify-content: center;
}

.lms_tab_contained .lms_tab_label {
  font-family: "Pretendard", sans-serif;
  font-weight: 600;
  line-height: 1.2;
  font-size: inherit;
  color: var(--lms-on-surface-low, rgba(0, 0, 0, 0.35));
}

.lms_tab_contained .lms_tab_item.active {
  background-color: var(--lms-on-surface-high-var, rgba(0, 0, 0, 0.7));
}

.lms_tab_contained .lms_tab_item.active .lms_tab_label {
  color: var(--lms-on-primary-high, #ffffff);
}

.lms_tab_contained .lms_tab_item:hover:not(.active):not(.disabled) {
  background-color: var(--lms-grey-200, #f1f2f2);
}

/* Count badge */
.lms_tab_count {
  font-family: "Pretendard", sans-serif;
  font-size: inherit;
  font-weight: 600;
  color: var(--lms-on-surface-medium-var, rgba(0, 0, 0, 0.5));
}

/* Disabled */
.lms_tab_item.disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

@media (max-width: 768px) {
  .lms_tab_wrap {
    overflow-x: auto;
    flex-wrap: nowrap;
  }

  .lms_tab_text .lms_tab_item { 
    flex: 0 0 auto;
    padding: 12px 0;
    min-width: 90px;
  }

  .lms_tab_text .lms_tab_label {
    white-space: nowrap;
  }
}
</style>
